import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import CountPill from '@/components/CountPill/CountPill'
import {
  StyledListItem,
  StyledListItemButton,
  StyledListItemIcon,
  StyledListItemText,
} from '@/components/Sidebar/Sidebar.styled'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui'
import { Button } from '@/components/ui/button'
import {
  useNavigationBlock,
  useNavigationBlockContext,
} from '@/features/file-upload'
import { SidebarItemConfig } from '@/features/sidebar/types'
import { cn } from '@/utils/common'
import { matchesRoute, RoutePath } from '@/utils/routes'

export interface SidebarItemProps {
  config: SidebarItemConfig
  onClick?: () => void
  isCollapsed?: boolean
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  config,
  onClick,
  isCollapsed = false,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { handleNavigationAttempt } = useNavigationBlock()
  const { triggerNavigationDialog } = useNavigationBlockContext()

  // Check if current pathname matches the route (including nested routes)
  const isActive = matchesRoute(location.pathname, config.path as RoutePath)

  const handleClick = () => {
    if (onClick) {
      onClick()
      return
    }

    // Check if navigation should be blocked
    const shouldProceed = handleNavigationAttempt(config.path)

    if (shouldProceed) {
      navigate(config.path)
    } else {
      // Show cancel dialog
      triggerNavigationDialog(config.path)
    }
  }

  const IconComponent = config.icon

  return (
    <StyledListItem>
      {isCollapsed ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="text"
              className={cn(
                'size-8 p-5 hover:bg-white mx-auto',
                isActive && 'bg-white'
              )}
              onClick={handleClick}
              aria-label={t(config.label)}
            >
              {IconComponent && (
                <StyledListItemIcon>
                  <IconComponent
                    size={20}
                    color="var(--Colors-Foreground-fg-quaternary-400)"
                  />
                </StyledListItemIcon>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">{t(config.label)}</TooltipContent>
        </Tooltip>
      ) : (
        <StyledListItemButton
          onClick={handleClick}
          selected={isActive}
          className=""
        >
          {IconComponent && (
            <StyledListItemIcon>
              <IconComponent
                size={20}
                color="var(--Colors-Foreground-fg-quaternary-400)"
              />
            </StyledListItemIcon>
          )}
          <StyledListItemText primary={t(config.label)} />
          {config.badge !== undefined && <CountPill count={config.badge} />}
        </StyledListItemButton>
      )}
    </StyledListItem>
  )
}

SidebarItem.displayName = 'SidebarItem'

export default React.memo(SidebarItem, (prevProps, nextProps) => {
  // Only re-render when config or isCollapsed changes
  return (
    prevProps.config === nextProps.config &&
    prevProps.isCollapsed === nextProps.isCollapsed
  )
})
